import type { AudioNote, CalendarEvent } from '@highlight/common'
import { getIsEventCanceled, getIsUserAttending } from './connections/calendars'

/**
 * Normalize URL for comparison - removes protocol, trailing slash, and query params
 */
export const normalizeUrl = (url: string): string => {
  return url
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '')
    .split('?')[0]
}

/**
 * Extract Google Meet code from a URL
 * Matches patterns like abc-defg-hij, on both meet.google.com/<code> and meet.google.com/meet/<code>
 */
export const extractGoogleMeetCode = (url: string): string | null => {
  // Match patterns like abc-defg-hij or any other format
  // Handle URLs with or without protocol
  const match = url.match(/(?:https?:\/\/)?meet\.google\.com\/(?:meet\/)?([\w-]+)/i)
  if (match) {
    // Remove query parameters and return just the meeting code
    return match[1].split('?')[0]
  }
  return null
}

export interface MeetingMatchInput {
  url?: string
  title?: string
}

/**
 * Only consider events that are ongoing, or start/end within this distance from now.
 * 2h: long enough to match the most egregious overruns, short enough to not match the entire day.
 */
export const MATCH_TIME_WINDOW_MS = 2 * 60 * 60 * 1000

/** Below this, title overlap is meaningless ("1:1", "PM"). Kept at the long-standing 4 —
 * the time window and closest-pick now carry the precision load; raise only with
 * `title source: ai` breadcrumb data showing short-title false positives. */
export const MIN_TITLE_MATCH_LENGTH = 4

/** Distance from `now` to the event's [start, end] interval; 0 while ongoing.
 * Unparseable dates yield Infinity so a malformed event can never win a match. */
const getDistanceFromNowMs = (event: CalendarEvent, nowMs: number): number => {
  const startMs = new Date(event.start).getTime()
  const endMs = new Date(event.end).getTime()
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return Infinity
  if (nowMs < startMs) return startMs - nowMs
  if (nowMs > endMs) return nowMs - endMs
  return 0
}

const DAY_MS = 24 * 60 * 60 * 1000

/** All-day events span the whole day at distance 0, so they'd otherwise beat every timed
 * event; treat anything a day or longer as all-day even if the flag wasn't serialized. */
const isAllDayEvent = (event: CalendarEvent): boolean => {
  if (event.isAllDay) return true
  const durationMs = new Date(event.end).getTime() - new Date(event.start).getTime()
  return durationMs >= DAY_MS
}

const pickClosestEvent = (events: CalendarEvent[], nowMs: number): CalendarEvent | null => {
  if (events.length === 0) return null
  return events.reduce((closest, event) => {
    // All-day events are a last resort: any timed candidate beats them.
    const closestAllDay = isAllDayEvent(closest)
    if (closestAllDay !== isAllDayEvent(event)) {
      return closestAllDay ? event : closest
    }
    const eventDistance = getDistanceFromNowMs(event, nowMs)
    const closestDistance = getDistanceFromNowMs(closest, nowMs)
    if (eventDistance !== closestDistance) {
      return eventDistance < closestDistance ? event : closest
    }
    // Two events equally close, such as both ongoing, fall back to attendance.
    if (!getIsUserAttending(closest) && getIsUserAttending(event)) return event
    return closest
  })
}

const isUrlMatch = (event: CalendarEvent, normalizedMeetingUrl: string, meetingCode: string | null): boolean => {
  if (!event.conferenceData?.uri) return false

  if (normalizeUrl(event.conferenceData.uri) === normalizedMeetingUrl) {
    return true
  }

  // If it's Google Meet, compare meeting codes
  if (meetingCode && event.conferenceData.name === 'Google Meet') {
    return extractGoogleMeetCode(event.conferenceData.uri) === meetingCode
  }

  return false
}

const isTitleMatch = (event: CalendarEvent, cleanTitle: string): boolean => {
  const eventTitle = event.title.toLowerCase()
  const minLength = Math.min(cleanTitle.length, eventTitle.length)
  if (minLength < MIN_TITLE_MATCH_LENGTH) return false
  return cleanTitle.includes(eventTitle) || eventTitle.includes(cleanTitle)
}

/**
 * Match a meeting to a calendar event from provided events.
 * Uses URL matching (including Google Meet code extraction) with a title-containment fallback.
 *
 * Precision-first: skips cancelled events and events outside MATCH_TIME_WINDOW_MS, and when
 * several candidates share a URL (e.g. back-to-back meetings in one personal Meet room)
 * picks the one temporally closest to `now`, using attendance only to break distance ties.
 * Joining a declined event's exact conference URL is strong evidence the user is attending
 * despite the RSVP, so an ongoing declined event beats a stale accepted one — but declined
 * events never match by title, nor do colleague events the user isn't on. A wrong link is
 * sticky server-side, so returning null beats returning the wrong event.
 */
export function findMatchingCalendarEvent(
  meeting: MeetingMatchInput,
  calendarEvents: CalendarEvent[] | undefined,
  now: Date = new Date(),
): CalendarEvent | null {
  if (!calendarEvents || calendarEvents.length === 0) {
    return null
  }

  const nowMs = now.getTime()
  const eligibleEvents = calendarEvents.filter(
    (event) => !getIsEventCanceled(event) && getDistanceFromNowMs(event, nowMs) <= MATCH_TIME_WINDOW_MS,
  )
  const acceptedEvents = eligibleEvents.filter(getIsUserAttending)

  // Strategy 1: Match by URL if available
  if (meeting.url) {
    const normalizedMeetingUrl = normalizeUrl(meeting.url)
    const meetingCode = extractGoogleMeetCode(meeting.url)
    const byUrl = (event: CalendarEvent) => isUrlMatch(event, normalizedMeetingUrl, meetingCode)

    const matchingEvent = pickClosestEvent(eligibleEvents.filter(byUrl), nowMs)
    if (matchingEvent) return matchingEvent
  }

  // Strategy 2: Match by window title (fallback for non-browser meetings or when URL is
  // missing). Title containment is too weak a signal to override a declined RSVP.
  if (meeting.title) {
    const cleanTitle = meeting.title
      .toLowerCase()
      .replace(/^meet [-–] /i, '') // Remove "Meet - " prefix from Google Meet
      .trim()

    const titleMatches = acceptedEvents.filter((event) => isTitleMatch(event, cleanTitle))
    const matchingEvent = pickClosestEvent(titleMatches, nowMs)
    if (matchingEvent) return matchingEvent
  }

  return null
}

/**
 * Backfill the calendar event behind a note that never recorded one — older
 * notes, and detected-meeting recordings the system couldn't resolve to an event
 * at capture time.
 *
 * This is `findMatchingCalendarEvent` anchored on the note's start rather than
 * `now`, which is the whole difference between "which meeting am I in?" and
 * "which meeting was that recording": the time window, the closest-pick, and the
 * declined/cancelled filtering all want to be relative to when the note was
 * taken. Falls back from the window title to the note's own title, since a
 * detected meeting carries the former and a saved note usually the latter.
 *
 * Returns undefined when the note already carries an event id — there is
 * nothing to backfill.
 */
export function findCalendarEventForNote(
  note: AudioNote | null | undefined,
  calendarEvents: CalendarEvent[] | undefined,
): CalendarEvent | null {
  if (!note?.meeting || note.meeting.calendarEventId) return null

  const notedAt = new Date(note.startedAt)
  return (
    findMatchingCalendarEvent({ url: note.meeting.url, title: note.meeting.windowTitle }, calendarEvents, notedAt) ??
    findMatchingCalendarEvent({ title: note.title }, calendarEvents, notedAt)
  )
}
